import { Card, CardContent, CardFooter, CardHeader, CardTitle } from 'components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import React from 'react';
import { EditingContext } from '../../containers/EditingContext';
import { Button } from 'components/ui/button';
import UserList from './UserList';
import { omit } from 'utils/index';
import { Link, useNavigate, useParams } from 'react-router';
import { ArrowLeft } from 'lucide-react';
import OrganizationForm from 'components/Forms/Organization';
import AccessGroups from './AccessGroups';
import OrgSettings from './OrgSettings';

const HasUnsavedContent = ({
  data,
}: {
  data?: Partial<IOrganizationFormData & { users: IOrg['users'] } & { accessGroups: IOrg['accessGroups'] }>;
}) => {
  return data && Object.keys(data).length > 0 ? (
    <div className="flex w-full justify-end text-white">
      <div className="px-2 pt-1 h-6 text-sm bg-gray-800 dark:bg-gray-600 rounded-md">Unsaved changes</div>
    </div>
  ) : null;
};

const OrgCard = () => {
  const { orgId } = useParams();

  const { data, submitNewData, selectOrg } = React.useContext(EditingContext);

  const thisOrgData = data?.[orgId || ''];

  const [changedInformation, setChangedInformation] = React.useState<
    Partial<IOrganizationFormData & { users: IOrg['users'] } & { accessGroups: IOrg['accessGroups'] }>
  >({});

  const onSubmitChange = () => {
    if (Object.keys(changedInformation).length > 0) {
      submitNewData(
        thisOrgData.orgId,
        changedInformation as Partial<IOrg> /** Some data might be different than in the interfaces, as some string arrays are strings */,
      )?.then(() => {
        setChangedInformation({});
      });
    }
  };

  const onChangeValue = (name: string, value: any) => {
    setChangedInformation({ ...changedInformation, [name]: value });
  };

  const hasChangedInformation = () => {
    return Object.keys(changedInformation).length > 0;
  };

  const navigate = useNavigate();

  React.useEffect(() => {
    if (!thisOrgData) {
      navigate('/');
    }
  }, []);

  return thisOrgData ? (
    <div>
      <Link to="/" onClick={() => selectOrg(null)}>
        <Button variant="outline">
          <ArrowLeft />
          <div>Back</div>
        </Button>
      </Link>
      <Card className="my-7">
        <CardHeader>
          <CardTitle>
            {thisOrgData.displayName}
            {` / `}
            {thisOrgData.orgId}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-0">
              <AccordionTrigger>
                <div className="w-full">General</div>
                <HasUnsavedContent data={omit(['users', 'orgSettings', 'accessGroups'], changedInformation)} />
              </AccordionTrigger>
              <AccordionContent className="p-3 pb-6">
                <OrganizationForm
                  data={{
                    ...{
                      ...thisOrgData,
                      platforms: thisOrgData.platforms,
                      externalAdmissionGroups: (thisOrgData.externalAdmissionGroups || []).join(','),
                      autocreateAlternateIdTypes: (thisOrgData.autocreateAlternateIdTypes || []).join(','),
                    },
                    ...changedInformation,
                  }}
                  onChangeData={(field, value) => setChangedInformation({ ...changedInformation, [field]: value })}
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-1">
              <AccordionTrigger>
                <div className="w-full">Org settings</div>
              </AccordionTrigger>
              <AccordionContent className="p-3 pb-6">
                <OrgSettings thisOrgData={thisOrgData} />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger>
                <div className="w-full">Users</div>
                <HasUnsavedContent data={changedInformation.users ? { users: changedInformation.users } : undefined} />
              </AccordionTrigger>
              <AccordionContent className="p-3 pb-6">
                <UserList
                  orgData={thisOrgData}
                  newUsers={changedInformation['users']}
                  onChangeUsers={users => onChangeValue('users', users)}
                />
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger>
                <div className="w-full">Access groups</div>
                <HasUnsavedContent
                  data={changedInformation.accessGroups ? { accessGroups: changedInformation.accessGroups } : undefined}
                />
              </AccordionTrigger>
              <AccordionContent className="p-3 pb-6">
                <AccessGroups
                  accessGroups={thisOrgData.accessGroups || []}
                  newAccessGroups={changedInformation['accessGroups']}
                  onChangeGroups={groups => onChangeValue('accessGroups', groups)}
                />
              </AccordionContent>
            </AccordionItem>
            {/* <AccordionItem value="item-4">
              <AccordionTrigger>Templates</AccordionTrigger>
              <AccordionContent className="p-3 pb-6">
                <p>Templates jne</p>
                {/* <HasUnsavedContent sectionName="templates" data={changedInformation} />}
              </AccordionContent>
            </AccordionItem> 
            */}
          </Accordion>
        </CardContent>
        {hasChangedInformation() && (
          <CardFooter className="justify-end">
            <div>
              <Button variant={'outline'} onClick={() => setChangedInformation({})}>
                Cancel all changes
              </Button>
            </div>
            <div className="ml-5">
              <Button onClick={() => onSubmitChange()}>Save all changes</Button>
            </div>
          </CardFooter>
        )}
      </Card>
    </div>
  ) : (
    <></>
  );
};

export default OrgCard;
